/**
 * Level18Scene - 关卡18：虫洞穿越
 * @description 教学目标：理解虫洞概念——连接两个时空点的隧道
 * 核心概念：虫洞是广义相对论中的理论结构，可连接两个遥远空间点实现瞬间跨越
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import { PhysicsWorld } from '@/physics/PhysicsWorld.ts';
import { Stickman } from '@/characters/Stickman.ts';
import { Blackhole } from '@/physics/Blackhole.ts';
import type { LevelConfig } from '@/levels/index.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';

const GAME_WIDTH = 1200;
const GAME_HEIGHT = 600;

type GameState = 'idle' | 'running' | 'teleporting' | 'success' | 'failed';

// 虫洞配置接口
interface WormholeConfig {
  id: string;
  position: { x: number; y: number };
  radius: number;
  mass: number;
  type: 'entry' | 'exit';
  label: string;
  color: number;
}

// 时空涟漪配置接口
interface SpacetimeRippleConfig {
  id: string;
  position: { x: number; y: number };
  radius: number;
  intensity: number;
}

// 障碍物配置接口
interface ObstacleConfig {
  id: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  type: 'static' | 'dynamic';
  color: number;
}

// 关卡特定配置（使用索引签名以兼容 LevelSpecificConfig）
interface Level18SpecificConfig {
  [key: string]: unknown;
  wormholeEntryPosition?: { x: number; y: number };
  wormholeExitPosition?: { x: number; y: number };
  teleportDuration?: number;
  spacetimeDistortionEnabled?: boolean;
  wormholeRadius?: number;
  showWormholeConcept?: boolean;
  hint?: string;
}

// 扩展关卡配置接口
interface Level18Config extends LevelConfig {
  wormholes?: WormholeConfig[];
  spacetimeRipples?: SpacetimeRippleConfig[];
  obstacles?: ObstacleConfig[];
}

/**
 * Level18Scene 类
 * 关卡18场景，实现虫洞穿越教学
 */
export class Level18Scene extends Phaser.Scene {
  // 关卡配置
  private levelConfig: Level18Config | null = null;
  private levelSpecific: Level18SpecificConfig | null = null;
  private wormholes: WormholeConfig[] = [];
  private spacetimeRipples: SpacetimeRippleConfig[] = [];
  private obstacles: ObstacleConfig[] = [];

  // 游戏状态
  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;

  // 物理世界
  private physicsWorld!: PhysicsWorld;
  private stickman!: Stickman;

  // 黑洞对象（虫洞入口/出口）
  private entryBlackhole!: Blackhole;
  private exitBlackhole!: Blackhole;

  // 游戏对象
  private wormholeEntryGraphics!: Phaser.GameObjects.Graphics;
  private wormholeExitGraphics!: Phaser.GameObjects.Graphics;
  private spacetimeRippleGraphics!: Phaser.GameObjects.Graphics;
  private distortionEffectGraphics!: Phaser.GameObjects.Graphics;
  private tunnelGraphics!: Phaser.GameObjects.Graphics;
  private goalZone!: Phaser.Geom.Rectangle;

  // 传送效果
  private isTeleporting: boolean = false;
  private teleportProgress: number = 0;
  private teleportStartTime: number = 0;
  private teleportDuration: number = 1500;

  // 时空扭曲效果
  private distortionPhase: number = 0;

  // UI 元素
  private hintText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;

  // 教程组件
  private tutorialUI: TutorialUI | null = null;

  // 是否胜利
  private isVictory: boolean = false;

  // 轨迹记录
  private trajectoryPoints: Array<{ x: number; y: number }> = [];
  private trajectoryGraphics!: Phaser.GameObjects.Graphics;

  constructor() {
    super({ key: 'Level18Scene' });
  }

  preload(): void {
    this.load.json('level-18', 'src/levels/levels/level-18.json');
  }

  create(): void {
    // 加载关卡配置
    this.levelConfig = this.cache.json.get('level-18') as Level18Config;

    if (this.levelConfig) {
      this.wormholes = this.levelConfig.wormholes ?? [];
      this.spacetimeRipples = this.levelConfig.spacetimeRipples ?? [];
      this.obstacles = this.levelConfig.obstacles ?? [];
      
      if (this.levelConfig.levelSpecific) {
        this.levelSpecific = this.levelConfig.levelSpecific;
        this.teleportDuration = this.levelSpecific.teleportDuration ?? 1500;
      }
    }

    // 初始化物理世界
    this.physicsWorld = new PhysicsWorld({
      gravity: Vec2(
        this.levelConfig?.physics.gravity.x ?? 0,
        this.levelConfig?.physics.gravity.y ?? -5
      ),
      allowSleep: this.levelConfig?.physics.allowSleep ?? true,
    });

    // 初始化黑洞对象
    const entryWormhole = this.wormholes.find(w => w.type === 'entry');
    const exitWormhole = this.wormholes.find(w => w.type === 'exit');

    if (entryWormhole) {
      this.entryBlackhole = new Blackhole({
        id: entryWormhole.id,
        position: Vec2(entryWormhole.position.x, entryWormhole.position.y),
        mass: entryWormhole.mass,
      });
    }

    if (exitWormhole) {
      this.exitBlackhole = new Blackhole({
        id: exitWormhole.id,
        position: Vec2(exitWormhole.position.x, exitWormhole.position.y),
        mass: exitWormhole.mass,
      });
    }

    // 创建图形层
    this.wormholeEntryGraphics = this.add.graphics();
    this.wormholeEntryGraphics.setDepth(10);
    
    this.wormholeExitGraphics = this.add.graphics();
    this.wormholeExitGraphics.setDepth(10);
    
    this.spacetimeRippleGraphics = this.add.graphics();
    this.spacetimeRippleGraphics.setDepth(5);
    
    this.distortionEffectGraphics = this.add.graphics();
    this.distortionEffectGraphics.setDepth(6);
    
    this.tunnelGraphics = this.add.graphics();
    this.tunnelGraphics.setDepth(3);
    
    this.trajectoryGraphics = this.add.graphics();
    this.trajectoryGraphics.setDepth(4);

    // 创建场景元素
    this.createBackground();
    this.createBoundaries();
    this.createPlatforms();
    this.createObstacles();
    this.createWormholes();
    this.createSpacetimeRipples();
    this.createGoalZone();
    this.createStickman();
    this.createUI();
    this.setupInput();

    // 初始化状态
    this.gameState = 'running';
    this.elapsedTime = 0;
    this.isTeleporting = false;
    this.teleportProgress = 0;
    this.isVictory = false;
    this.trajectoryPoints = [];

    // 启动视觉效果循环
    this.startVisualEffects();

    // 显示教程
    this.showTutorial();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState === 'success' && this.isVictory) return;

    const dt = delta / 1000;

    if (this.gameState === 'running') {
      this.elapsedTime += dt;
      
      // 物理步进
      this.physicsWorld.step(dt);

      // 更新火柴人渲染
      this.stickman.update();

      // 更新轨迹
      this.updateTrajectory();

      // 检测虫洞穿越
      this.checkWormholeEntry();

      // 检测胜利条件
      this.checkVictory();
    } else if (this.gameState === 'teleporting') {
      // 处理传送效果
      this.updateTeleportEffect(_time);
    }

    // 更新时空扭曲效果
    this.updateDistortionEffect();

    // 更新虫洞可视化
    this.updateWormholeVisualization();
  }

  /**
   * 创建背景（深空主题）
   */
  private createBackground(): void {
    this.cameras.main.setBackgroundColor('#050510');

    // 星星背景
    const stars = this.add.graphics();
    for (let i = 0; i < 150; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * GAME_HEIGHT;
      const size = Math.random() * 2 + 0.5;
      const brightness = Math.random() * 0.5 + 0.3;

      stars.fillStyle(0xffffff, brightness);
      stars.fillCircle(x, y, size);
    }

    // 星云效果
    const nebula = this.add.graphics();
    nebula.fillStyle(0x4a0080, 0.05);
    nebula.fillCircle(200, 300, 100);
    nebula.fillStyle(0x004080, 0.05);
    nebula.fillCircle(1000, 300, 100);

    // 网格背景（时空网格）
    const grid = this.add.graphics();
    grid.lineStyle(1, 0x2a2a4a, 0.1);
    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      grid.beginPath();
      grid.moveTo(x, 0);
      grid.lineTo(x, GAME_HEIGHT);
      grid.strokePath();
    }
    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      grid.beginPath();
      grid.moveTo(0, y);
      grid.lineTo(GAME_WIDTH, y);
      grid.strokePath();
    }
  }

  /**
   * 创建边界
   */
  private createBoundaries(): void {
    this.physicsWorld.createBody('boundary', {
      x: GAME_WIDTH / 2,
      y: 0,
      type: 'static',
      isBoundary: true,
      boundarySize: { width: GAME_WIDTH, height: GAME_HEIGHT },
    });

    const boundaryGraphics = this.add.graphics();
    boundaryGraphics.lineStyle(2, 0x333333);
    boundaryGraphics.strokeRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
  }

  /**
   * 创建平台
   */
  private createPlatforms(): void {
    const platforms = this.levelConfig?.platforms ?? [];
    const platformGraphics = this.add.graphics();

    for (const platform of platforms) {
      this.physicsWorld.createBody(platform.id, {
        x: platform.position.x,
        y: platform.position.y,
        type: 'static',
        friction: platform.friction ?? 0.5,
        restitution: platform.restitution ?? 0,
      });

      const screenX = platform.position.x - platform.size.width / 2;
      const screenY = GAME_HEIGHT - platform.position.y - platform.size.height / 2;
      const color = platform.color ?? 0x4a4a6a;

      platformGraphics.fillStyle(color, 1);
      platformGraphics.fillRect(screenX, screenY, platform.size.width, platform.size.height);
    }
  }

  /**
   * 创建障碍物（阻止直接到达终点）
   */
  private createObstacles(): void {
    const obstacleGraphics = this.add.graphics();
    obstacleGraphics.setDepth(2);

    for (const obstacle of this.obstacles) {
      this.physicsWorld.createBody(obstacle.id, {
        x: obstacle.position.x,
        y: obstacle.position.y,
        type: 'static',
        friction: 0.5,
        restitution: 0,
      });

      const screenX = obstacle.position.x - obstacle.size.width / 2;
      const screenY = GAME_HEIGHT - obstacle.position.y - obstacle.size.height / 2;

      obstacleGraphics.fillStyle(obstacle.color, 1);
      obstacleGraphics.fillRect(screenX, screenY, obstacle.size.width, obstacle.size.height);

      // 障碍物标签
      this.add.text(obstacle.position.x, obstacle.position.y - obstacle.size.height / 2 + 10, '⛔', {
        fontSize: '12px',
        color: '#ff0000',
      }).setOrigin(0.5);
    }
  }

  /**
   * 创建虫洞（入口和出口）
   */
  private createWormholes(): void {
    // 绘制虫洞隧道连接线
    this.tunnelGraphics.lineStyle(2, 0x6a6a8a, 0.3);
    const entryPos = this.wormholes.find(w => w.type === 'entry')?.position;
    const exitPos = this.wormholes.find(w => w.type === 'exit')?.position;

    if (entryPos && exitPos) {
      // 直线隧道效果（简化）
      this.tunnelGraphics.beginPath();
      this.tunnelGraphics.moveTo(entryPos.x, GAME_HEIGHT - entryPos.y);
      // 中间点
      const midX = (entryPos.x + exitPos.x) / 2;
      const midY = GAME_HEIGHT / 2 - 100;
      this.tunnelGraphics.lineTo(midX, midY);
      this.tunnelGraphics.lineTo(exitPos.x, GAME_HEIGHT - exitPos.y);
      this.tunnelGraphics.strokePath();

      // 隧道标签
      this.add.text(midX, midY - 20, '⚡ 时空隧道 ⚡', {
        fontSize: '12px',
        color: '#6a6a8a',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }

    // 绘制虫洞入口
    for (const wormhole of this.wormholes) {
      this.drawWormhole(wormhole);
    }
  }

  /**
   * 绘制单个虫洞
   */
  private drawWormhole(wormhole: WormholeConfig): void {
    const graphics = wormhole.type === 'entry' ? this.wormholeEntryGraphics : this.wormholeExitGraphics;
    const screenY = GAME_HEIGHT - wormhole.position.y;

    // 外圈（时空扭曲效果）
    graphics.lineStyle(3, wormhole.color, 0.8);
    graphics.strokeCircle(wormhole.position.x, screenY, wormhole.radius);

    // 内圈（事件视界）
    graphics.fillStyle(wormhole.color, 0.4);
    graphics.fillCircle(wormhole.position.x, screenY, wormhole.radius * 0.8);

    // 核心（深色中心）
    graphics.fillStyle(0x000000, 1);
    graphics.fillCircle(wormhole.position.x, screenY, wormhole.radius * 0.3);

    // 光环效果（虫洞特征）
    const glowColor = wormhole.type === 'entry' ? 0x8866cc : 0xcc88ff;
    graphics.lineStyle(4, glowColor, 0.5);
    graphics.strokeCircle(wormhole.position.x, screenY, wormhole.radius * 1.2);

    // 标签
    const labelColor = wormhole.type === 'entry' ? '#8866cc' : '#cc88ff';
    this.add.text(wormhole.position.x, screenY - wormhole.radius - 20, wormhole.label, {
      fontSize: '16px',
      color: labelColor,
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
  }

  /**
   * 创建时空涟漪效果
   */
  private createSpacetimeRipples(): void {
    // 时空涟漪区域可视化
    const rippleGraphics = this.add.graphics();
    rippleGraphics.setDepth(4);

    for (const ripple of this.spacetimeRipples) {
      const screenY = GAME_HEIGHT - ripple.position.y;
      
      // 涟漪圈
      rippleGraphics.lineStyle(1, 0x4a4a8a, 0.2);
      for (let r = ripple.radius * 0.5; r <= ripple.radius; r += 20) {
        rippleGraphics.strokeCircle(ripple.position.x, screenY, r);
      }
    }
  }

  /**
   * 创建目标区域
   */
  private createGoalZone(): void {
    const goal = this.levelConfig?.goal;
    if (!goal) return;

    const screenX = goal.position.x - goal.size.width / 2;
    const screenY = GAME_HEIGHT - goal.position.y - goal.size.height / 2;

    this.goalZone = new Phaser.Geom.Rectangle(
      screenX,
      screenY,
      goal.size.width,
      goal.size.height
    );

    const goalGraphics = this.add.graphics();
    goalGraphics.setDepth(1);
    goalGraphics.fillStyle(goal.color ?? 0x4ade80, 0.5);
    goalGraphics.fillRectShape(this.goalZone);
    goalGraphics.lineStyle(3, goal.color ?? 0x4ade80);
    goalGraphics.strokeRectShape(this.goalZone);

    this.add.text(this.goalZone.centerX, this.goalZone.y - 15, '🎯 终点', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  /**
   * 创建火柴人
   */
  private createStickman(): void {
    const playerConfig = this.levelConfig?.player;
    const startY = playerConfig?.startPosition.y ?? 300;
    const startX = playerConfig?.startPosition.x ?? 100;

    this.stickman = new Stickman(
      this,
      this.physicsWorld,
      {
        x: startX,
        y: GAME_HEIGHT - startY,
        scale: 1,
        walkSpeed: 8,
        jumpForce: 150,
        colors: {
          line: 0xffa500,
          headFill: 0x1a1a2e,
          lineWidth: 3,
        },
      },
      GAME_HEIGHT
    );
  }

  /**
   * 创建 UI
   */
  private createUI(): void {
    // 标题
    this.add.text(GAME_WIDTH / 2, 30, '关卡18：虫洞穿越', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 概念说明
    this.add.text(GAME_WIDTH / 2, 60, '虫洞：连接两个时空点的隧道', {
      fontSize: '16px',
      color: '#6b8cff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 状态文本
    this.statusText = this.add.text(10, 10, '状态: 探索虫洞', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
      backgroundColor: '#1a1a2e',
      padding: { x: 5, y: 3 },
    });

    // 提示文本
    this.hintText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 50,
      this.levelSpecific?.hint ?? '障碍物阻挡了通往终点的路径，必须通过虫洞穿越！',
      {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Arial',
        backgroundColor: '#1a1a2e',
        padding: { x: 10, y: 5 },
      }
    ).setOrigin(0.5);

    // 物理原理说明区域
    this.createPhysicsInfo();
  }

  /**
   * 创建物理原理说明
   */
  private createPhysicsInfo(): void {
    const infoBox = this.add.graphics();
    infoBox.fillStyle(0x2a2a4a, 0.6);
    infoBox.fillRect(GAME_WIDTH - 350, GAME_HEIGHT - 150, 340, 130);

    this.add.text(GAME_WIDTH - 330, GAME_HEIGHT - 135, '💡 虫洞概念', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    });

    this.add.text(GAME_WIDTH - 330, GAME_HEIGHT - 110, '虫洞（Einstein-Rosen桥）是广义相对论中的理论结构', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    this.add.text(GAME_WIDTH - 330, GAME_HEIGHT - 90, '理论上可连接两个遥远空间点，实现瞬间跨越', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    this.add.text(GAME_WIDTH - 330, GAME_HEIGHT - 70, '入口 → 时空隧道 → 出口（瞬间传送）', {
      fontSize: '12px',
      color: '#8866cc',
      fontFamily: 'Arial',
    });

    this.add.text(GAME_WIDTH - 330, GAME_HEIGHT - 50, '⚠️ 注意：现实中尚未发现虫洞存在的证据', {
      fontSize: '10px',
      color: '#888888',
      fontFamily: 'Arial',
    });
  }

  /**
   * 设置输入事件
   */
  private setupInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'r' || event.key === 'R') {
        this.resetLevel();
      }
    });
  }

  /**
   * 启动视觉效果循环
   */
  private startVisualEffects(): void {
    // 时空涟漪动画
    this.time.addEvent({
      delay: 50,
      callback: () => {
        this.distortionPhase += 0.1;
      },
      loop: true,
    });

    // 虫洞脉冲动画
    this.time.addEvent({
      delay: 100,
      callback: () => {
        this.updateWormholePulse();
      },
      loop: true,
    });
  }

  /**
   * 更新虫洞脉冲效果
   */
  private updateWormholePulse(): void {
    // 在 updateWormholeVisualization 中处理
  }

  /**
   * 更新虫洞可视化
   */
  private updateWormholeVisualization(): void {
    this.wormholeEntryGraphics.clear();
    this.wormholeExitGraphics.clear();

    for (const wormhole of this.wormholes) {
      this.drawAnimatedWormhole(wormhole);
    }
  }

  /**
   * 绘制动画虫洞
   */
  private drawAnimatedWormhole(wormhole: WormholeConfig): void {
    const graphics = wormhole.type === 'entry' ? this.wormholeEntryGraphics : this.wormholeExitGraphics;
    const screenY = GAME_HEIGHT - wormhole.position.y;
    const pulsePhase = Math.sin(this.distortionPhase) * 0.3 + 0.7;

    // 外圈脉冲
    const pulseRadius = wormhole.radius * (1 + Math.sin(this.distortionPhase * 2) * 0.05);
    graphics.lineStyle(4, wormhole.color, pulsePhase);
    graphics.strokeCircle(wormhole.position.x, screenY, pulseRadius);

    // 内圈旋转效果
    const rotationAngle = this.distortionPhase * 0.5;
    graphics.lineStyle(2, wormhole.color, 0.6);
    graphics.strokeCircle(wormhole.position.x, screenY, wormhole.radius * 0.8);

    // 核心（深色）
    graphics.fillStyle(0x000000, 1);
    graphics.fillCircle(wormhole.position.x, screenY, wormhole.radius * 0.3);

    // 光环效果
    const glowColor = wormhole.type === 'entry' ? 0x8866cc : 0xcc88ff;
    graphics.lineStyle(3, glowColor, 0.4 * pulsePhase);
    graphics.strokeCircle(wormhole.position.x, screenY, wormhole.radius * 1.3);

    // 螺旋线效果（虫洞特征）
    const spiralCount = 4;
    for (let i = 0; i < spiralCount; i++) {
      const angle = rotationAngle + (i * Math.PI * 2 / spiralCount);
      const innerR = wormhole.radius * 0.3;
      const outerR = wormhole.radius * 0.9;
      
      graphics.lineStyle(1, glowColor, 0.3);
      graphics.beginPath();
      graphics.moveTo(
        wormhole.position.x + Math.cos(angle) * innerR,
        screenY + Math.sin(angle) * innerR
      );
      
      // 螺旋到外圈
      for (let t = 0; t <= 1; t += 0.1) {
        const r = innerR + (outerR - innerR) * t;
        const a = angle + t * Math.PI;
        graphics.lineTo(
          wormhole.position.x + Math.cos(a) * r,
          screenY + Math.sin(a) * r
        );
      }
      graphics.strokePath();
    }
  }

  /**
   * 更新时空扭曲效果
   */
  private updateDistortionEffect(): void {
    this.distortionEffectGraphics.clear();

    if (!this.levelSpecific?.spacetimeDistortionEnabled) return;

    // 环绕虫洞的时空涟漪
    for (const ripple of this.spacetimeRipples) {
      const screenY = GAME_HEIGHT - ripple.position.y;
      const intensity = ripple.intensity;

      // 多层涟漪效果
      for (let layer = 0; layer < 5; layer++) {
        const phaseOffset = layer * 0.5;
        const layerRadius = ripple.radius * (0.3 + layer * 0.2) + Math.sin(this.distortionPhase + phaseOffset) * 10;
        const alpha = intensity * (0.3 - layer * 0.05);

        this.distortionEffectGraphics.lineStyle(2, 0x4a4a8a, alpha);
        this.distortionEffectGraphics.strokeCircle(ripple.position.x, screenY, layerRadius);
      }
    }

    // 传送时的特效
    if (this.isTeleporting) {
      this.drawTeleportEffect();
    }
  }

  /**
   * 绘制传送效果
   */
  private drawTeleportEffect(): void {
    const entryPos = this.wormholes.find(w => w.type === 'entry')?.position;
    const exitPos = this.wormholes.find(w => w.type === 'exit')?.position;

    if (!entryPos || !exitPos) return;

    const progress = this.teleportProgress;

    // 时空隧道可视化（传送进度）
    this.distortionEffectGraphics.lineStyle(4, 0x8866cc, progress);
    
    // 从入口到出口的隧道效果
    const currentX = entryPos.x + (exitPos.x - entryPos.x) * progress;
    const currentY = GAME_HEIGHT - entryPos.y + (GAME_HEIGHT - exitPos.y - (GAME_HEIGHT - entryPos.y)) * progress;

    // 粒子效果
    for (let i = 0; i < 20; i++) {
      const particleProgress = Math.max(0, Math.min(1, progress + (Math.random() - 0.5) * 0.3));
      const px = entryPos.x + (exitPos.x - entryPos.x) * particleProgress;
      const py = GAME_HEIGHT - entryPos.y + (GAME_HEIGHT - exitPos.y - (GAME_HEIGHT - entryPos.y)) * particleProgress;
      
      this.distortionEffectGraphics.fillStyle(0xcc88ff, 0.5);
      this.distortionEffectGraphics.fillCircle(px, py, 3 + Math.random() * 2);
    }

    // 中心光点
    this.distortionEffectGraphics.fillStyle(0xffffff, progress);
    this.distortionEffectGraphics.fillCircle(currentX, currentY, 10 + progress * 5);

    // 螺旋隧道效果
    const spiralRadius = 30 * progress;
    for (let angle = 0; angle < Math.PI * 4; angle += 0.2) {
      const spiralX = currentX + Math.cos(angle + this.distortionPhase * 3) * spiralRadius * (1 - angle / (Math.PI * 4));
      const spiralY = currentY + Math.sin(angle + this.distortionPhase * 3) * spiralRadius * (1 - angle / (Math.PI * 4));
      
      this.distortionEffectGraphics.fillStyle(0x8866cc, 0.3 * progress);
      this.distortionEffectGraphics.fillCircle(spiralX, spiralY, 2);
    }
  }

  /**
   * 更新轨迹
   */
  private updateTrajectory(): void {
    if (this.isTeleporting) return;

    const headPos = this.stickman.getHeadPosition();
    this.trajectoryPoints.push({ x: headPos.x, y: headPos.y });

    if (this.trajectoryPoints.length > 100) {
      this.trajectoryPoints.shift();
    }

    this.trajectoryGraphics.clear();

    if (this.trajectoryPoints.length > 1) {
      this.trajectoryGraphics.lineStyle(2, 0xffa500, 0.3);

      const firstPoint = this.trajectoryPoints[0];
      if (firstPoint) {
        this.trajectoryGraphics.beginPath();
        this.trajectoryGraphics.moveTo(firstPoint.x, firstPoint.y);
      }

      for (let i = 1; i < this.trajectoryPoints.length; i++) {
        const point = this.trajectoryPoints[i];
        if (point) {
          this.trajectoryGraphics.lineTo(point.x, point.y);
        }
      }

      this.trajectoryGraphics.strokePath();
    }
  }

  /**
   * 检测虫洞入口
   */
  private checkWormholeEntry(): void {
    const headPos = this.stickman.getHeadPosition();
    const entryWormhole = this.wormholes.find(w => w.type === 'entry');

    if (!entryWormhole) return;

    const entryScreenY = GAME_HEIGHT - entryWormhole.position.y;
    const distance = Math.sqrt(
      Math.pow(headPos.x - entryWormhole.position.x, 2) +
      Math.pow(headPos.y - entryScreenY, 2)
    );

    // 进入虫洞入口范围
    if (distance < entryWormhole.radius * 0.8 && !this.isTeleporting) {
      this.startTeleport();
    }
  }

  /**
   * 开始传送
   */
  private startTeleport(): void {
    this.isTeleporting = true;
    this.gameState = 'teleporting';
    this.teleportStartTime = this.time.now;
    this.teleportProgress = 0;

    // 隐藏火柴人渲染
    this.stickman.getRenderer().getGraphics().setVisible(false);

    this.statusText.setText('状态: 正在穿越虫洞... ⚡');
    this.hintText.setText('时空隧道正在传送你...');

    // 传送声音效果（视觉替代）
    this.cameras.main.flash(200, 100, 50, 200);
  }

  /**
   * 更新传送效果
   */
  private updateTeleportEffect(currentTime: number): void {
    const elapsed = currentTime - this.teleportStartTime;
    this.teleportProgress = Math.min(1, elapsed / this.teleportDuration);

    if (this.teleportProgress >= 1) {
      this.completeTeleport();
    }
  }

  /**
   * 完成传送
   */
  private completeTeleport(): void {
    this.isTeleporting = false;
    this.gameState = 'running';

    // 获取出口位置
    const exitWormhole = this.wormholes.find(w => w.type === 'exit');
    if (!exitWormhole) return;

    const exitScreenY = GAME_HEIGHT - exitWormhole.position.y;

    // 传送火柴人到出口位置
    const torso = this.stickman.getBody().getBody('torso');
    if (torso) {
      torso.setPosition(Vec2(exitWormhole.position.x, exitScreenY));
      torso.setLinearVelocity(Vec2(0, 0));
    }

    // 恢复火柴人渲染
    this.stickman.getRenderer().getGraphics().setVisible(true);

    // 清空轨迹
    this.trajectoryPoints = [];

    // 更新状态
    this.statusText.setText('状态: 成功穿越虫洞！ ✓');
    this.hintText.setText('你已成功穿越虫洞！现在前往终点！');

    // 传送完成特效
    this.cameras.main.flash(200, 200, 100, 50);
    this.cameras.main.shake(100, 0.01);
  }

  /**
   * 检测胜利条件
   */
  private checkVictory(): void {
    if (this.isVictory) return;

    const headPos = this.stickman.getHeadPosition();

    if (this.goalZone.contains(headPos.x, headPos.y)) {
      this.isVictory = true;
      this.gameState = 'success';
      this.showVictoryMessage();
    }
  }

  /**
   * 显示胜利消息
   */
  private showVictoryMessage(): void {
    const saveGameState = getGameState();
    saveGameState.completeLevel(18, this.elapsedTime);

    // 胜利背景
    const victoryBg = this.add.graphics();
    victoryBg.fillStyle(0x000000, 0.8);
    victoryBg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 100, '🎉 虫洞穿越成功！', {
      fontSize: '36px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 - 50,
      '你成功通过虫洞到达了终点！',
      {
        fontSize: '18px',
        color: '#ffffff',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 - 20,
      `用时：${this.elapsedTime.toFixed(1)} 秒`,
      {
        fontSize: '16px',
        color: '#edc240',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 10,
      '虫洞概念：连接两个时空点的隧道，实现瞬间跨越',
      {
        fontSize: '14px',
        color: '#8866cc',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    this.createCompletionButtons();
  }

  /**
   * 创建完成按钮
   */
  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 60;

    const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, y, '下一关', {
      fontSize: '20px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    nextBtn.on('pointerdown', () => {
      const gameState = getGameState();
      if (gameState.isLevelUnlocked(19)) {
        this.scene.start('Level19Scene');
      } else {
        this.scene.start('LevelSelectScene');
      }
    });

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, y, '返回', {
      fontSize: '20px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });

    const retryBtn = this.add.text(GAME_WIDTH / 2, y + 40, '重新尝试', {
      fontSize: '16px',
      color: '#ffa500',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    retryBtn.on('pointerdown', () => {
      this.resetLevel();
    });
  }

  /**
   * 重置关卡
   */
  private resetLevel(): void {
    this.scene.restart();
  }

  /**
   * 显示教程
   */
  private showTutorial(): void {
    const tutorialData = getTutorial('level-18');
    if (!tutorialData) {
      // 如果没有教程数据，显示默认教程
      this.showDefaultTutorial();
      return;
    }

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 18,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  /**
   * 显示默认教程（如果没有配置）
   */
  private showDefaultTutorial(): void {
    const tutorialBg = this.add.graphics();
    tutorialBg.fillStyle(0x000000, 0.8);
    tutorialBg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 100, '关卡18：虫洞穿越', {
      fontSize: '32px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 50, '虫洞：连接两个时空点的隧道', {
      fontSize: '18px',
      color: '#6b8cff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, '理论上可实现瞬间跨越遥远空间', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 10, '🎯 目标：通过虫洞到达终点', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 30, '💡 操作：进入左侧虫洞入口，瞬间传送到右侧出口', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    const startBtn = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 70, '开始探索', {
      fontSize: '20px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    startBtn.on('pointerdown', () => {
      tutorialBg.destroy();
      startBtn.destroy();
    });
  }

  // 公开访问方法
  getGameState(): GameState {
    return this.gameState;
  }

  getElapsedTime(): number {
    return this.elapsedTime;
  }

  getIsTeleporting(): boolean {
    return this.isTeleporting;
  }

  getTeleportProgress(): number {
    return this.teleportProgress;
  }

  getEntryBlackhole(): Blackhole {
    return this.entryBlackhole;
  }

  getExitBlackhole(): Blackhole {
    return this.exitBlackhole;
  }

  getStickman(): Stickman {
    return this.stickman;
  }

  getPhysicsWorld(): PhysicsWorld {
    return this.physicsWorld;
  }

  getIsVictory(): boolean {
    return this.isVictory;
  }
}